import React, { useRef, useState } from 'react';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
  FaCropAlt,
} from 'react-icons/fa';
import { parseClipTimestamp } from './clipTimeUtils';
import './player-controls.css';

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const formatClock = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Seek bar: drag/click to seek, hover preview time, clip ranges as bands.
 * onSeek(seconds, final) — final=false while dragging, true on release.
 */
function SeekBar({ current, duration, buffered = 0, clips = [], onSeek }) {
  const trackRef = useRef(null);
  const [dragValue, setDragValue] = useState(null);
  const [hover, setHover] = useState(null);
  const dragging = useRef(false);

  const hasDuration = duration > 0;
  const shown = dragValue ?? current;
  const pct = hasDuration ? clamp((shown / duration) * 100, 0, 100) : 0;

  const timeAt = (clientX) => {
    const b = trackRef.current.getBoundingClientRect();
    const f = clamp((clientX - b.left) / (b.width || 1), 0, 1);
    return { t: f * duration, f };
  };

  const onPointerDown = (e) => {
    if (!hasDuration || (e.pointerType === 'mouse' && e.button !== 0)) return;
    dragging.current = true;
    try { trackRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const { t } = timeAt(e.clientX);
    setDragValue(t);
    onSeek(t, false);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!hasDuration) return;
    const { t, f } = timeAt(e.clientX);
    setHover({ t, f });
    if (!dragging.current) return;
    setDragValue(t);
    onSeek(t, false);
  };

  const endDrag = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    try { trackRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const { t } = timeAt(e.clientX);
    setDragValue(null);
    onSeek(t, true);
  };

  const onKeyDown = (e) => {
    if (!hasDuration) return;
    const step = e.shiftKey ? 10 : 5;
    let next = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = current - step;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = current + step;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = duration;
    if (next == null) return;
    e.preventDefault();
    e.stopPropagation();
    onSeek(clamp(next, 0, duration), true);
  };

  const bands = hasDuration
    ? clips
      .map((c) => ({ s: parseClipTimestamp(c.startTime), e: parseClipTimestamp(c.endTime) }))
      .filter((c) => c.s != null && c.e != null && c.e > c.s)
      .map((c) => ({
        left: clamp((c.s / duration) * 100, 0, 100),
        width: clamp(((c.e - c.s) / duration) * 100, 0, 100),
      }))
    : [];

  return (
    <div
      ref={trackRef}
      className={`pc-seek ${dragValue != null ? 'is-dragging' : ''} ${hasDuration ? '' : 'is-disabled'}`}
      role="slider"
      tabIndex={hasDuration ? 0 : -1}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration || 0)}
      aria-valuenow={Math.round(shown || 0)}
      aria-valuetext={formatClock(shown)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => setHover(null)}
      onKeyDown={onKeyDown}
    >
      <div className="pc-seek__track">
        <div className="pc-seek__buffered" style={{ width: `${clamp(buffered * 100, 0, 100)}%` }} />
        {bands.map((b, i) => (
          <div key={i} className="pc-seek__band" style={{ left: `${b.left}%`, width: `${Math.max(b.width, 0.4)}%` }} />
        ))}
        <div className="pc-seek__fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="pc-seek__thumb" style={{ left: `${pct}%` }} />
      {hover && dragValue == null && (
        <div className="pc-seek__tip" style={{ left: `${hover.f * 100}%` }}>{formatClock(hover.t)}</div>
      )}
    </div>
  );
}

/** Custom player controls rendered BELOW the video (never over it). */
export default function PlayerControls({
  playing,
  onTogglePlay,
  current,
  duration,
  buffered,
  onSeek,
  onSeekBy,
  muted,
  volume,
  onToggleMute,
  onVolume,
  rate,
  onRate,
  onFullscreen,
  isFullscreen,
  clips,
  onCrop,
  cropActive = false,
}) {
  const nextRate = () => {
    const i = PLAYBACK_RATES.indexOf(rate);
    onRate(PLAYBACK_RATES[(i + 1) % PLAYBACK_RATES.length]);
  };

  return (
    <div className="pc" role="group" aria-label="Video controls">
      <SeekBar current={current} duration={duration} buffered={buffered} clips={clips} onSeek={onSeek} />

      <div className="pc__row">
        <div className="pc__group">
          <button
            type="button"
            className="pc-btn pc-btn--play"
            onClick={onTogglePlay}
            aria-label={playing ? 'Pause' : 'Play'}
            title={playing ? 'Pause (Space)' : 'Play (Space)'}
          >
            {playing ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
          </button>
          <button type="button" className="pc-btn pc-btn--text" onClick={() => onSeekBy(-5)} title="Back 5 seconds">−5s</button>
          <button type="button" className="pc-btn pc-btn--text" onClick={() => onSeekBy(5)} title="Forward 5 seconds">+5s</button>
          <span className="pc__time">
            <span>{formatClock(current)}</span>
            <span className="pc__time-sep">/</span>
            <span className="pc__time-total">{formatClock(duration)}</span>
          </span>
        </div>

        <div className="pc__group pc__group--end">
          <div className="pc-volume">
            <button
              type="button"
              className="pc-btn"
              onClick={onToggleMute}
              aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
              title={muted || volume === 0 ? 'Unmute' : 'Mute'}
            >
              {muted || volume === 0 ? <FaVolumeMute aria-hidden="true" /> : <FaVolumeUp aria-hidden="true" />}
            </button>
            <input
              className="pc-volume__range"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(e) => onVolume(Number(e.target.value))}
              aria-label="Volume"
              style={{ '--pc-vol': `${(muted ? 0 : volume) * 100}%` }}
            />
          </div>
          <button type="button" className="pc-btn pc-btn--text pc-rate" onClick={nextRate} title="Playback speed">
            {rate}×
          </button>
          {onCrop && (
            <button
              type="button"
              className={`pc-btn pc-btn--crop ${cropActive ? 'is-active' : ''}`}
              onClick={onCrop}
              aria-pressed={cropActive}
              title={cropActive ? 'Close crop' : 'Crop video'}
            >
              <FaCropAlt aria-hidden="true" />
              <span>Crop</span>
            </button>
          )}
          <button
            type="button"
            className="pc-btn"
            onClick={onFullscreen}
            aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullscreen ? <FaCompress aria-hidden="true" /> : <FaExpand aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
}
